import { isLoggedInAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { LogoRenderer } from 'app/design/branding/logo';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { useCreateIdentity } from 'app/network/identity';
import { useResetPassword } from 'app/network/user';
import type { NavigationScreen, SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { type ResetPasswordDTO } from 'app/types/user';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import { useEffect, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import * as yup from 'yup';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type FormSchema = Omit<ResetPasswordDTO, 'email'>;

const schema: yup.ObjectSchema<FormSchema> = yup.object({
  code: yup
    .string()
    .length(6)
    .matches(/[0-9]{6}/)
    .required(),
  password: yup.string().password().required(),
});

const MINT_SCREEN_BG = theme.colors['surface.tertiary'];
const CARD_MAX_WIDTH = 440;

function ChangePasswordCta({
  disabled,
  isLoading,
  onPress,
  placement,
  t,
}: {
  disabled: boolean;
  isLoading: boolean;
  onPress: () => void;
  placement: 'card' | 'accessory';
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  return (
    <Button
      disabled={disabled}
      loading={isLoading}
      {...(placement === 'card'
        ? { marginTop: 'spacing.general.x12' }
        : { marginHorizontal: 'spacing.general.x4', marginTop: 'spacing.general.x4' })}
      onPress={onPress}
      variant="primary"
    >
      <Text color="surface.default" fontWeight="700" textAlign="center">
        {t('primaryButtonText')}
      </Text>
    </Button>
  );
}

function ResetPasswordFields({
  control,
  route,
  t,
}: {
  control: Control<FormSchema>;
  route: SpecificRouteProp<'reset-password'>;
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  return (
    <>
      <Text fontWeight="700" marginBottom="spacing.general.x4" textAlign="center" variant="headerXSmall">
        {t('title')}
      </Text>

      {!route.params.code ? (
        <Text
          color="text.primary"
          marginBottom="spacing.general.x5"
          opacity={0.85}
          textAlign="center"
          variant="bodyBase"
        >
          {t('details')}
        </Text>
      ) : null}

      <Column gap="spacing.general.x4">
        <Controller
          name="code"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              editable={!route.params.code}
              error={fieldState.error}
              fieldBorderRadius="radius.full"
              keyboardType="number-pad"
              label={t('codeFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              placeholder={t('codeFieldPlaceholder')}
              textContentType="oneTimeCode"
              value={field.value}
            />
          )}
        />

        <Controller
          {...{ control }}
          name="password"
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect={false}
              error={fieldState.error}
              fieldBorderRadius="radius.full"
              label={t('passwordFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              placeholder={t('passwordFieldPlaceholder')}
              secureTextEntry
              textContentType="newPassword"
              value={field.value}
            />
          )}
        />
      </Column>
    </>
  );
}

export const ResetPasswordScreenComponent = function ResetPasswordScreenComponent({
  navigation,
  route,
}: {
  navigation: SpecificNavigationProp<'reset-password'>;
  route: SpecificRouteProp<'reset-password'>;
}) {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { isLargeUI } = useUISize();
  const { control, formState, handleSubmit, getValues } = useForm<FormSchema>({
    schema,
    defaultValues: { code: route.params.code },
  });

  const setPageTitle = usePageTitle('reset-password');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const [formError, setFormError] = useState<Error>();

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [goBackWhenIsLoggedIn, setGoBackWhenIsLoggedIn] = useState(false);
  useEffect(() => {
    if (!goBackWhenIsLoggedIn) return;
    if (!isLoggedIn) return;

    navigation.navigate('main', { screen: 'billboard' }, { pop: true });
  }, [goBackWhenIsLoggedIn, isLoggedIn, navigation]);

  const { mutate: login, isPending: isLoggingIn } = useCreateIdentity({
    onSuccess: () => {
      track(Event.LoginSuccess, { provider: 'Email' });
      setGoBackWhenIsLoggedIn(true);
    },
  });

  const { isPending: isLoading, mutate } = useResetPassword({
    onSuccess: () => {
      login({
        email: route.params.email,
        password: getValues('password'),
      });
    },
    onError: (error) => setFormError(error),
  });

  const onSubmit = handleSubmit((form) => {
    setFormError(undefined);
    mutate({ ...form, email: route.params.email });
  });

  const isSubmitting = isLoading || isLoggingIn || goBackWhenIsLoggedIn;

  if (isLargeUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1, backgroundColor: MINT_SCREEN_BG }}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingHorizontal: theme.spacing['spacing.general.x6'],
            paddingVertical: theme.spacing['spacing.general.x12'],
            width: '100%',
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={{
              alignSelf: 'center',
              backgroundColor: theme.colors['surface.default'],
              borderRadius: theme.borderRadii['radius.xl'],
              maxWidth: CARD_MAX_WIDTH,
              paddingHorizontal: theme.spacing['spacing.general.x8'],
              paddingVertical: theme.spacing['spacing.general.x10'],
              width: '100%',
              ...(Platform.OS === 'web'
                ? {
                    shadowColor: '#1F2635',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.08,
                    shadowRadius: 32,
                  }
                : {
                    elevation: 4,
                    shadowColor: '#1F2635',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.08,
                    shadowRadius: 16,
                  }),
            }}
          >
            <ResetPasswordFields control={control} route={route} t={t} />

            {formError ? (
              <View style={{ marginTop: theme.spacing['spacing.general.x4'] }}>
                <RemoteError {...{ error: formError, message: tAPI(formError.message) }} />
              </View>
            ) : null}

            <ChangePasswordCta
              disabled={!formState.isValid}
              isLoading={isSubmitting}
              onPress={() => void onSubmit()}
              placement="card"
              t={t}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  return (
    <Page
      accessory={
        <Accessory>
          {formError ? (
            <View style={{ marginHorizontal: theme.spacing['spacing.general.x4'] }}>
              <RemoteError {...{ error: formError, message: tAPI(formError.message) }} />
            </View>
          ) : null}

          <ChangePasswordCta
            disabled={!formState.isValid}
            isLoading={isSubmitting}
            onPress={() => void onSubmit()}
            placement="accessory"
            t={t}
          />
        </Accessory>
      }
      style={{ backgroundColor: theme.colors['surface.default'] }}
    >
      <Column marginHorizontal="spacing.general.x4" marginTop="spacing.general.x2">
        <ResetPasswordFields control={control} route={route} t={t} />
      </Column>
    </Page>
  );
};

const ResetPasswordScreenRouteComponent: NavigationScreen<'reset-password'> = ({ navigation, route }) => {
  const { isLargeUI } = useUISize();
  const { t } = useTranslation('accessibility');
  const headerForegroundColor = theme.colors['text.primary'];

  useNavigationOptions(navigation, {
    headerBackVisible: false,
    headerLeft: () => (
      <Button onPress={navigation.goBack} accessibilityLabel={isLargeUI ? t('resetPassword.close') : t('goBack')}>
        <IconX strokeWidth={ICON_STROKE_WIDTH} color={headerForegroundColor} />
      </Button>
    ),
    headerStyle: {
      backgroundColor: isLargeUI ? MINT_SCREEN_BG : theme.colors['surface.tertiary-neutral'],
    },
    headerTintColor: headerForegroundColor,
    headerTitle: isLargeUI
      ? () => null
      : () => (
          <View style={{ justifyContent: 'center', paddingVertical: 4 }}>
            <LogoRenderer color={headerForegroundColor} width={132} />
          </View>
        ),
    headerTitleAlign: isLargeUI ? 'center' : 'left',
  });

  return <ResetPasswordScreenComponent navigation={navigation} route={route} />;
};

const ResetPasswordScreenWithErrorBoundary = withErrorBoundary(ResetPasswordScreenRouteComponent);

export const ResetPasswordScreen: NavigationScreen<'reset-password'> = (props) => (
  <ResetPasswordScreenWithErrorBoundary {...props} />
);
